import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { recordAPI, booksAPI, auditLogAPI, archiveAPI, corporateAPI, governanceAPI, libraryAPI } from '../api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Search, FileText, FolderOpen, Book, Shield, Loader2,
  Archive, BookOpen, Send, Landmark, Scale, Mail, GraduationCap,
} from 'lucide-react';
import { toast } from 'sonner';

// Tab components
import { CorporateBooksTab } from '../components/records/CorporateBooksTab';
import { MeetingDocsTab } from '../components/records/MeetingDocsTab';
import { GovernanceTab } from '../components/records/GovernanceTab';
import { RegulatoryDocsTab } from '../components/records/RegulatoryDocsTab';
import { CommunicationsTab } from '../components/records/CommunicationsTab';
import { OtherResourcesTab } from '../components/records/OtherResourcesTab';
import { ArchivedTab } from '../components/records/ArchivedTab';
// Dialogs
import { UploadDialog } from '../components/records/UploadDialog';
import { ArchiveSettingsDialog } from '../components/records/ArchiveSettingsDialog';

const Records = () => {
  const { user } = useAuth();
  const isSecretariat = ['corporate_secretary', 'secretariat_staff', 'super_admin', 'landlord'].includes(user?.role);

  const [records, setRecords] = useState([]);
  const [minutesBook, setMinutesBook] = useState([]);
  const [resolutionsBook, setResolutionsBook] = useState([]);
  const [noticeLogs, setNoticeLogs] = useState([]);
  const [proxyLogs, setProxyLogs] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [resources, setResources] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [sharedItems, setSharedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('corporate-books');
  const [govSubFilter, setGovSubFilter] = useState('all');

  // Archive state
  const [archiveSettings, setArchiveSettings] = useState(null);
  const [archiveSettingsOpen, setArchiveSettingsOpen] = useState(false);
  const [savingArchive, setSavingArchive] = useState(false);
  const [archiveForm, setArchiveForm] = useState({
    frequency: 'quarterly', retention_days: 365, download_destination: 'computer',
    cloud_provider: '', cloud_api_key: '', cloud_folder_id: '',
    reminder_days_before: 7, auto_archive_enabled: true, notify_on_archive: true,
  });

  // Upload state
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadTab, setUploadTab] = useState('');
  const [uploadForm, setUploadForm] = useState({ title: '', record_type: '', classification: 'internal', description: '', gov_subcategory: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [savingUpload, setSavingUpload] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      if (!isSecretariat) {
        const res = await governanceAPI.getPortalSharedItems().catch(() => ({ data: [] }));
        setSharedItems(res.data || []);
        setLoading(false);
        return;
      }
      const [recordsRes, minRes, resRes, noticeRes, proxyRes, archRes, credRes, resrcRes, commRes] = await Promise.all([
        recordAPI.getAll({}).catch(() => ({ data: [] })),
        booksAPI.getMinutesBook().catch(() => ({ data: [] })),
        booksAPI.getResolutionsBook().catch(() => ({ data: [] })),
        booksAPI.getNoticeLog().catch(() => ({ data: [] })),
        booksAPI.getProxyLog().catch(() => ({ data: [] })),
        archiveAPI.getSettings().catch(() => ({ data: null })),
        corporateAPI.getCredentials().catch(() => ({ data: [] })),
        corporateAPI.getResources().catch(() => ({ data: [] })),
        corporateAPI.getCommunications().catch(() => ({ data: [] })),
      ]);
      setRecords(recordsRes.data || []);
      setMinutesBook(minRes.data || []);
      setResolutionsBook(resRes.data || []);
      setNoticeLogs(noticeRes.data || []);
      setProxyLogs(proxyRes.data || []);
      setCredentials(credRes.data || []);
      setResources((resrcRes.data || []).filter(r => r.published));
      setCommunications(commRes.data || []);
      if (archRes.data) {
        setArchiveSettings(archRes.data);
        setArchiveForm(prev => ({
          ...prev,
          frequency: archRes.data.frequency || 'quarterly',
          retention_days: archRes.data.retention_days || 365,
          download_destination: archRes.data.download_destination || 'computer',
          cloud_provider: archRes.data.cloud_provider || '',
          reminder_days_before: archRes.data.reminder_days_before || 7,
          auto_archive_enabled: archRes.data.auto_archive_enabled !== false,
          notify_on_archive: archRes.data.notify_on_archive !== false,
        }));
      }
    } catch (err) { console.error('Fetch error:', err); }
    finally { setLoading(false); }
  };

  const handleArchiveRecords = async (recordIds) => {
    try { await archiveAPI.execute({ record_ids: recordIds }); toast.success('Archived'); fetchData(); }
    catch { toast.error('Failed to archive'); }
  };

  const handleSaveArchiveSettings = async () => {
    setSavingArchive(true);
    try {
      const payload = {
        frequency: archiveForm.frequency, retention_days: parseInt(archiveForm.retention_days) || 365,
        download_destination: archiveForm.download_destination, cloud_provider: archiveForm.cloud_provider || null,
        reminder_days_before: parseInt(archiveForm.reminder_days_before) || 7,
        auto_archive_enabled: archiveForm.auto_archive_enabled, notify_on_archive: archiveForm.notify_on_archive,
      };
      if (archiveForm.cloud_provider && archiveForm.cloud_api_key) {
        payload.cloud_credentials = { api_key: archiveForm.cloud_api_key, folder_id: archiveForm.cloud_folder_id };
      }
      const res = await archiveAPI.updateSettings(payload);
      setArchiveSettings(res.data);
      setArchiveSettingsOpen(false);
      toast.success('Archive settings saved');
    } catch { toast.error('Failed to save settings'); }
    finally { setSavingArchive(false); }
  };

  const openUploadDialog = (tab, defaultType = '', defaultGov = '') => {
    setUploadTab(tab);
    setUploadForm({ title: '', record_type: defaultType, classification: 'internal', description: '', gov_subcategory: defaultGov });
    setUploadFile(null);
    setUploadDialog(true);
  };

  const handleUpload = async () => {
    if (!uploadForm.title.trim()) { toast.error('Title is required'); return; }
    setSavingUpload(true);
    try {
      const fd = new FormData();
      fd.append('title', uploadForm.title);
      fd.append('tab', uploadTab);
      fd.append('record_type', uploadForm.record_type || 'other');
      fd.append('classification', uploadForm.classification);
      fd.append('description', uploadForm.description);
      fd.append('gov_subcategory', uploadForm.gov_subcategory);
      if (uploadFile) fd.append('file', uploadFile);
      await libraryAPI.upload(fd);
      toast.success('Document uploaded to Library');
      setUploadDialog(false);
      fetchData();
    } catch { toast.error('Failed to upload'); }
    finally { setSavingUpload(false); }
  };

  const handleDeleteComm = async (id) => {
    try { await corporateAPI.deleteCommunication(id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  const term = searchTerm.toLowerCase();
  const activeRecords = records.filter(r => r.status !== 'archived');
  const archivedRecords = records.filter(r => r.status === 'archived');
  const certificates = activeRecords.filter(r => r.record_type === 'certificate');
  const reportorial = activeRecords.filter(r => r.record_type === 'filing');
  const governanceRecords = activeRecords.filter(r => ['policy', 'charter'].includes(r.record_type));

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" /></div>;

  // Stakeholder simplified view
  if (!isSecretariat) {
    const filteredShared = sharedItems.filter(i =>
      (i.name || i.title || '').toLowerCase().includes(term) || (i.credential_type || i.category || i.record_type || '').toLowerCase().includes(term)
    );
    const typeIcons = { credential: Shield, resource: FolderOpen, record: Book };
    return (
      <div className="space-y-6" data-testid="records-page">
        <div><h1 className="text-2xl font-bold text-[#1A1D21]">Library</h1><p className="text-[#5A6570] mt-1">Shared documents and resources</p></div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
          <Input placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-records-input" />
        </div>
        {filteredShared.length === 0 ? (
          <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
            <FolderOpen className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
            <h3 className="text-base font-medium text-[#1A1D21] mb-1">{searchTerm ? 'No matches' : 'No shared items yet'}</h3>
            <p className="text-sm text-[#5A6570]">The Secretariat will share items here when available</p>
          </CardContent></Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {filteredShared.map(item => {
              const Icon = typeIcons[item._type] || FileText;
              return (
                <Card key={`${item._type}-${item.id}`} className="border-[#E2E6EA]" data-testid={`shared-lib-${item.id}`}>
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="p-2 bg-[#F0F2F4] rounded-lg shrink-0"><Icon className="h-5 w-5 text-[#5A6570]" /></div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-[#1A1D21] truncate">{item.name || item.title}</h4>
                      <p className="text-[10px] text-[#A8B1B9] mt-0.5">{item._type}{item.credential_type ? ` - ${item.credential_type}` : ''}{item.category ? ` - ${item.category}` : ''}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // === Secretariat full view ===
  return (
    <div className="space-y-6" data-testid="records-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-[#1A1D21]">Library</h1><p className="text-[#5A6570] mt-1">Corporate governance documents, registers, and communications</p></div>
        <Button variant="outline" onClick={() => setArchiveSettingsOpen(true)} className="border-[#E2E6EA]" data-testid="archive-settings-btn">
          <Archive className="h-4 w-4 mr-2" />Archive Settings
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
        <Input placeholder="Search library..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-records-input" />
      </div>

      <Tabs value={activeTab} onValueChange={v => { setActiveTab(v); setGovSubFilter('all'); }}>
        <TabsList className="tabs-grid-6 bg-[#F0F2F4] p-1 h-auto" data-testid="records-tabs-list">
          <TabsTrigger value="corporate-books" className="data-[state=active]:bg-white text-xs py-2" data-testid="tab-corporate-books">
            <BookOpen className="h-3.5 w-3.5 mr-1" />Corporate Books
          </TabsTrigger>
          <TabsTrigger value="meeting-docs" className="data-[state=active]:bg-white text-xs py-2" data-testid="tab-meeting-docs">
            <Send className="h-3.5 w-3.5 mr-1" />Meeting Docs
          </TabsTrigger>
          <TabsTrigger value="governance" className="data-[state=active]:bg-white text-xs py-2" data-testid="tab-governance">
            <Landmark className="h-3.5 w-3.5 mr-1" />Governance
          </TabsTrigger>
          <TabsTrigger value="regulatory" className="data-[state=active]:bg-white text-xs py-2" data-testid="tab-regulatory">
            <Scale className="h-3.5 w-3.5 mr-1" />Regulatory Docs
          </TabsTrigger>
          <TabsTrigger value="communications" className="data-[state=active]:bg-white text-xs py-2" data-testid="tab-communications">
            <Mail className="h-3.5 w-3.5 mr-1" />Communications
          </TabsTrigger>
          <TabsTrigger value="other-resources" className="data-[state=active]:bg-white text-xs py-2" data-testid="tab-other-resources">
            <GraduationCap className="h-3.5 w-3.5 mr-1" />Other Resources
          </TabsTrigger>
        </TabsList>

        <div className="flex items-center gap-2 mt-2">
          <Button variant={activeTab === 'archived' ? 'default' : 'ghost'} size="sm"
            className={activeTab === 'archived' ? 'bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs' : 'h-7 text-xs text-[#5A6570]'}
            onClick={() => setActiveTab('archived')} data-testid="tab-archived">
            <Archive className="h-3 w-3 mr-1" />Archived ({archivedRecords.length})
          </Button>
        </div>

        <TabsContent value="corporate-books" className="mt-4">
          <CorporateBooksTab minutesBook={minutesBook} resolutionsBook={resolutionsBook} certificates={certificates} term={term} onArchive={handleArchiveRecords} onUploadOpen={openUploadDialog} />
        </TabsContent>

        <TabsContent value="meeting-docs" className="mt-4">
          <MeetingDocsTab noticeLogs={noticeLogs} proxyLogs={proxyLogs} term={term} onUploadOpen={openUploadDialog} />
        </TabsContent>

        <TabsContent value="governance" className="mt-4">
          <GovernanceTab credentials={credentials} governanceRecords={governanceRecords} term={term} govSubFilter={govSubFilter} setGovSubFilter={setGovSubFilter} onUploadOpen={openUploadDialog} />
        </TabsContent>

        <TabsContent value="regulatory" className="mt-4">
          <RegulatoryDocsTab reportorial={reportorial} term={term} onArchive={handleArchiveRecords} onUploadOpen={openUploadDialog} />
        </TabsContent>

        <TabsContent value="communications" className="mt-4">
          <CommunicationsTab communications={communications} term={term} onDeleteComm={handleDeleteComm} onUploadOpen={openUploadDialog} />
        </TabsContent>

        <TabsContent value="other-resources" className="mt-4">
          <OtherResourcesTab resources={resources} term={term} onUploadOpen={openUploadDialog} />
        </TabsContent>

        <TabsContent value="archived" className="mt-4">
          <ArchivedTab archivedRecords={archivedRecords} term={term} />
        </TabsContent>
      </Tabs>

      <UploadDialog open={uploadDialog} onOpenChange={setUploadDialog} uploadTab={uploadTab}
        uploadForm={uploadForm} setUploadForm={setUploadForm}
        uploadFile={uploadFile} setUploadFile={setUploadFile}
        onUpload={handleUpload} saving={savingUpload} />

      <ArchiveSettingsDialog open={archiveSettingsOpen} onOpenChange={setArchiveSettingsOpen}
        archiveForm={archiveForm} setArchiveForm={setArchiveForm}
        onSave={handleSaveArchiveSettings} saving={savingArchive} />
    </div>
  );
};

export default Records;
